import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, PremiumModal, Field, SelectField, Button,
  LoadingState, EmptyState,
} from "components/ui";

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconUpload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const IconSparkles = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" />
  </svg>
);
const IconImage = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);
const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const PRODUCTS_WITH_PENDING_POLL_MS = 3000;

const CategoryDetail = () => {
  const { id } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const productFileInputRef = useRef(null);

  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [productModal, setProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "", description: "", sku: "", barcode: "",
    price: "", cost: "", unit: "piece", categoryId: id,
  });
  const set = (k) => (e) => setProductForm((f) => ({ ...f, [k]: e.target.value }));

  // ── AI image generation state ───────────────────────────
  const [generatingIds, setGeneratingIds] = useState(new Set()); // per-product "Generate" button loading
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const pollRef = useRef(null);

  const fetchCategory = async () => {
    try {
      const res = await axiosPrivate.get(`/api/categories/${id}`);
      setCategory(res.data);
      fetchProducts();
    } catch (_) {
      toast.error("Category not found");
      navigate("/tenant/categories");
    } finally {
      setLoading(false);
    }
  };
  const fetchAllCategories = async () => {
    try {
      const res = await axiosPrivate.get("/api/categories");
      setCategories(res.data);
    } catch (_) {}
  };
  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await axiosPrivate.get("/api/products");
      setProducts(res.data.filter((p) => p.categoryId === id));
    } catch (_) {
      toast.error("Failed to load products");
    } finally {
      setProductsLoading(false);
    }
  }, [id]);

  // Lightweight refresh used by polling — skips the loading spinner so cards
  // don't flicker while we're just checking on PENDING image generations.
  const refreshProductsQuietly = useCallback(async () => {
    try {
      const res = await axiosPrivate.get("/api/products");
      setProducts(res.data.filter((p) => p.categoryId === id));
    } catch (_) {
      // silent — this is a background poll, don't spam toasts
    }
  }, [id]);

  useEffect(() => { fetchCategory(); fetchAllCategories(); }, [id]);

  // ── Poll while any product image is still generating ────
  const hasPendingImages = products.some((p) => p.imageStatus === "PENDING");
  useEffect(() => {
    if (hasPendingImages) {
      pollRef.current = setInterval(refreshProductsQuietly, PRODUCTS_WITH_PENDING_POLL_MS);
    }
    return () => clearInterval(pollRef.current);
  }, [hasPendingImages, refreshProductsQuietly]);

  const handleProductImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosPrivate.post(
        `/api/products/import?categoryId=${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const imported = response.data.imported || [];
      if (imported.length > 0) {
        toast.success(`${imported.length} products imported!`);
        fetchProducts();
      } else {
        toast.warn("No products were imported. Sheet name must match this category.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Import failed");
    }
    if (productFileInputRef.current) productFileInputRef.current.value = "";
  };

  const openProductModal = (prod = null) => {
    if (prod) {
      setEditingProduct(prod);
      setProductForm({
        name: prod.name || "", description: prod.description || "",
        sku: prod.sku || "", barcode: prod.barcode || "",
        price: prod.price || "", cost: prod.cost || "",
        unit: prod.unit || "piece", categoryId: prod.categoryId || id,
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: "", description: "", sku: "", barcode: "",
        price: "", cost: "", unit: "piece", categoryId: id,
      });
    }
    setProductModal(true);
  };

  const handleProductSave = async () => {
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price) || 0,
        cost: parseFloat(productForm.cost) || 0,
      };
      if (editingProduct) {
        await axiosPrivate.put(`/api/products/${editingProduct.id}`, payload);
        toast.success("Product updated");
      } else {
        await axiosPrivate.post("/api/products", payload);
        toast.success("Product created — generating image…");
      }
      setProductModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving product");
    }
  };

  const handleProductDelete = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axiosPrivate.delete(`/api/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  // ── Generate / regenerate image for a single product ────
  const handleGenerateImage = async (productId) => {
    setGeneratingIds((prev) => new Set(prev).add(productId));
    try {
      const res = await axiosPrivate.post(`/api/products/${productId}/generate-image`);
      setProducts((prev) => prev.map((p) => (p.id === productId ? res.data : p)));
      toast.success("Image generated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Image generation failed");
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // ── Bulk-generate images for every product in this category missing one ──
  const productsMissingImages = products.filter(
    (p) => !p.avatarUrl && p.imageStatus !== "PENDING"
  );

  const handleBulkGenerate = async () => {
    if (productsMissingImages.length === 0) return;
    if (!window.confirm(
      `Generate AI images for ${productsMissingImages.length} product(s) without an image?`
    )) return;

    setBulkGenerating(true);
    try {
      const res = await axiosPrivate.post("/api/products/generate-images/bulk");
      toast.success(`${res.data.enqueuedCount} image(s) queued for generation`);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Bulk generation failed to start");
    } finally {
      setBulkGenerating(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState /></PremiumCard>
      </PageContainer>
    );
  }
  if (!category) {
    return (
      <PageContainer>
        <PremiumCard><EmptyState title="Category not found" /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PremiumCard
        className="mb-3"
        title={category.name}
        subtitle={`Tax: ${category.taxName || "None"}`}
        actions={<Button variant="secondary" size="sm" onClick={() => navigate("/tenant/categories")}>← Back to categories</Button>}
      />

      <PremiumCard
        title={`Products (${products.length})`}
        actions={
          <>
            {productsMissingImages.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<IconSparkles />}
                onClick={handleBulkGenerate}
                disabled={bulkGenerating}
              >
                {bulkGenerating ? "Queuing…" : `Generate ${productsMissingImages.length} image(s)`}
              </Button>
            )}
            <Button variant="secondary" size="sm" leftIcon={<IconUpload />} onClick={() => productFileInputRef.current?.click()}>
              Import
            </Button>
            <input type="file" accept=".xlsx,.xls" ref={productFileInputRef} style={{ display: "none" }} onChange={handleProductImport} />
            <Button variant="primary" size="sm" leftIcon={<IconPlus />} onClick={() => openProductModal()}>Add product</Button>
          </>
        }
      >
        {productsLoading ? (
          <LoadingState />
        ) : products.length === 0 ? (
          <EmptyState
            title="No products in this category"
            description="Add one or import from Excel to get started."
            action={<Button leftIcon={<IconPlus />} onClick={() => openProductModal()}>Add product</Button>}
          />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            {products.map((prod) => {
              const isGenerating = generatingIds.has(prod.id) || prod.imageStatus === "PENDING";
              const isFailed = prod.imageStatus === "FAILED";

              return (
                <div
                  key={prod.id}
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: 14,
                    padding: 18,
                    display: "flex", flexDirection: "column", gap: 8,
                    transition: "box-shadow var(--duration-base) var(--ease), transform var(--duration-base) var(--ease)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "var(--shadow-md)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  {/* ── Product image / generation status ── */}
                  <div
                    style={{
                      width: "100%", height: 140, borderRadius: 10,
                      background: "var(--surface-subtle, rgba(0,0,0,0.03))",
                      border: "1px solid var(--border-subtle)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      overflow: "hidden", position: "relative",
                    }}
                  >
                    {isGenerating ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, color: "var(--text-muted)" }}>
                        <div className="t-spinner" style={{
                          width: 22, height: 22, borderRadius: "50%",
                          border: "2px solid var(--border-subtle)",
                          borderTopColor: "var(--brand-primary)",
                          animation: "spin 0.8s linear infinite",
                        }} />
                        <span className="t-body-sm">Generating image…</span>
                      </div>
                    ) : prod.avatarUrl ? (
                      <img
                        src={prod.avatarUrl}
                        alt={prod.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, color: "var(--text-muted)" }}>
                        {isFailed ? <IconAlert /> : <IconImage />}
                        <span className="t-body-sm">{isFailed ? "Generation failed" : "No image yet"}</span>
                        <Button
                          variant="ghost" size="sm" leftIcon={<IconSparkles />}
                          onClick={() => handleGenerateImage(prod.id)}
                        >
                          Generate
                        </Button>
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                    <h4 className="t-heading-sm" style={{ margin: 0 }}>{prod.name}</h4>
                    <span className="t-mono" style={{ color: "var(--brand-primary)", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {prod.price} TND
                    </span>
                  </div>
                  <div className="t-body-sm t-muted">
                    {prod.sku || "No SKU"} · {prod.unit} · {prod.barcode || "No barcode"}
                  </div>
                  {prod.description && (
                    <p className="t-body-sm t-muted" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", margin: 0 }}>
                      {prod.description}
                    </p>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: "auto", paddingTop: 8, borderTop: "1px solid var(--border-subtle)" }}>
                    {prod.avatarUrl && !isGenerating && (
                      <Button
                        variant="ghost" size="sm" leftIcon={<IconSparkles />}
                        onClick={() => handleGenerateImage(prod.id)}
                        disabled={generatingIds.has(prod.id)}
                      >
                        Regenerate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" leftIcon={<IconEdit />} onClick={() => openProductModal(prod)}>Edit</Button>
                    <Button variant="danger" size="sm" leftIcon={<IconTrash />} onClick={() => handleProductDelete(prod.id)}>Delete</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PremiumCard>

      <PremiumModal
        isOpen={productModal}
        toggle={() => setProductModal(false)}
        title={editingProduct ? "Edit product" : "New product"}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setProductModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleProductSave}>Save</Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Field label="Name" required value={productForm.name} onChange={set("name")} />
          <Field label="SKU" value={productForm.sku} onChange={set("sku")} />
          <Field label="Barcode" value={productForm.barcode} onChange={set("barcode")} />
          <SelectField label="Unit" value={productForm.unit} onChange={set("unit")}>
            <option value="piece">piece</option>
            <option value="kg">kg</option>
            <option value="liter">liter</option>
          </SelectField>
          <Field label="Price" type="number" step="0.01" value={productForm.price} onChange={set("price")} />
          <Field label="Cost"  type="number" step="0.01" value={productForm.cost}  onChange={set("cost")} />
          <SelectField label="Category" value={productForm.categoryId} onChange={set("categoryId")}>
            <option value="">— Select —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectField>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Description" as="textarea" rows={3} value={productForm.description} onChange={set("description")} />
          </div>
        </div>
      </PremiumModal>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </PageContainer>
  );
};

export default CategoryDetail;